import * as ToastPrimitive from '@radix-ui/react-toast';
import { type VariantProps, cva } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils';

export const ToastProvider = ToastPrimitive.Provider;

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      'fixed top-0 z-[300] flex max-h-screen w-full flex-col-reverse p-4',
      'sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]',
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const toastVariants = cva(
  cn(
    'group pointer-events-auto relative flex w-full items-center justify-between gap-3 overflow-hidden',
    'rounded-[4px] border p-4 pr-8 shadow-[var(--shadow-md)] transition-all',
    'data-[state=open]:animate-in data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full',
    'data-[swipe=end]:animate-out data-[swipe=cancel]:translate-x-0',
    'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
  ),
  {
    variants: {
      variant: {
        default: cn(
          'border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text-primary)]',
        ),
        success: cn(
          'border-[var(--color-success)]/40 bg-[var(--color-bg)]',
          'text-[var(--color-text-primary)] [&_svg]:text-[var(--color-success)]',
        ),
        error: cn(
          'border-[var(--color-error)]/40 bg-[var(--color-bg)]',
          'text-[var(--color-text-primary)] [&_svg]:text-[var(--color-error)]',
        ),
        warning: cn(
          'border-[var(--color-warning)]/40 bg-[var(--color-bg)]',
          'text-[var(--color-text-primary)] [&_svg]:text-[var(--color-warning)]',
        ),
        info: cn(
          'border-[var(--color-info)]/40 bg-[var(--color-bg)]',
          'text-[var(--color-text-primary)] [&_svg]:text-[var(--color-info)]',
        ),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = React.forwardRef<React.ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, variant, ...props }, ref) => (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  ),
);
Toast.displayName = ToastPrimitive.Root.displayName;

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      'inline-flex h-8 shrink-0 items-center justify-center rounded-[4px] border border-[var(--color-border)]',
      'bg-transparent px-3 text-[12px] font-medium',
      'hover:bg-[var(--color-surface)]',
      'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]',
      'disabled:pointer-events-none disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      'absolute right-2 top-2 rounded-[4px] p-1 text-[var(--color-text-secondary)] opacity-0 transition-opacity',
      'hover:text-[var(--color-text-primary)] group-hover:opacity-100',
      'focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]',
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" />
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn('text-[14px] font-semibold text-[var(--color-text-primary)]', className)}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn('text-[12px] text-[var(--color-text-secondary)]', className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

export { toastVariants };
